/**
 * Herkenbare betaalmethode-iconen voor klanten (Stripe Checkout — ingeschakelde methoden).
 */
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  type CustomerBrandId,
  CUSTOMER_BRAND_ORDER,
  CUSTOMER_METHOD_COPY,
} from '../../data/customer-payment-methods';

const iconWrap =
  'inline-flex h-9 max-w-[4.75rem] shrink-0 items-center justify-center rounded-md border border-border/80 bg-white px-1.5 shadow-sm dark:border-border dark:bg-white';

function SvgIdeal({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 52 20" className="h-5 w-[52px]" role="img" aria-labelledby={titleId}>
      <title id={titleId}>iDEAL</title>
      <rect width="52" height="20" rx="3" fill="#CC6600" />
      <text x="26" y="14" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="system-ui,sans-serif">
        iDEAL
      </text>
    </svg>
  );
}

function SvgBancontact({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 56 20" className="h-5 w-14" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Bancontact</title>
      <rect width="56" height="20" rx="3" fill="#1E64C8" />
      <rect x="6" y="5" width="10" height="10" rx="1" fill="white" opacity="0.95" />
      <rect x="18" y="5" width="10" height="10" rx="1" fill="white" opacity="0.75" />
      <rect x="30" y="5" width="10" height="10" rx="1" fill="white" opacity="0.55" />
    </svg>
  );
}

function SvgVisa({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 48 16" className="h-4 w-12" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Visa</title>
      <text x="24" y="12" textAnchor="middle" fill="#1A1F71" fontSize="11" fontWeight="800" fontStyle="italic" fontFamily="Arial,sans-serif">
        VISA
      </text>
    </svg>
  );
}

function SvgMastercard({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 40 24" className="h-6 w-10" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Mastercard</title>
      <circle cx="15" cy="12" r="9" fill="#EB001B" />
      <circle cx="25" cy="12" r="9" fill="#F79E1B" />
      <path d="M20 6.5a9 9 0 0 1 0 11 9 9 0 0 1 0-11z" fill="#FF5F00" opacity="0.9" />
    </svg>
  );
}

function SvgApplePay({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 72 22" className="h-[22px] w-[4.5rem]" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Apple Pay</title>
      <rect width="72" height="22" rx="3" fill="#000" />
      <text
        x="36"
        y="14.5"
        textAnchor="middle"
        fill="white"
        fontSize="7.5"
        fontWeight="600"
        fontFamily="system-ui,-apple-system,BlinkMacSystemFont,sans-serif"
      >
        Apple Pay
      </text>
    </svg>
  );
}

function SvgGooglePay({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 72 22" className="h-[22px] w-[4.5rem]" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Google Pay</title>
      <rect width="72" height="22" rx="3" fill="#fff" stroke="#dadce0" />
      <path
        fill="#4285F4"
        d="M11 8.5c1.4 0 2.6.5 3.5 1.4l2.6-2.5C15.3 5.8 13.3 5 11 5 7.1 5 3.8 7.2 2.4 10.5l3 2.3C6.2 10.4 8.4 8.5 11 8.5z"
      />
      <path
        fill="#34A853"
        d="M17.5 11c0 .7-.1 1.3-.3 1.9l3 2.3c.6-1.2 1-2.5 1-4.2 0-1-.2-1.9-.5-2.8l-3 2.3c.2.4.3.8.3 1.3z"
      />
      <path
        fill="#FBBC05"
        d="M11 15.5c-1.3 0-2.4-.8-2.9-1.9l-3 2.3c1.4 2.8 4.3 4.6 7.4 4.6 2.1 0 3.9-.7 5.2-1.9l-2.9-2.2c-.7.5-1.7.8-2.8.8z"
      />
      <path fill="#EA4335" d="M6.1 12.6c-.3-.8-.5-1.7-.5-2.6s.2-1.8.5-2.6l-3-2.3C2.3 6.7 1.8 8.3 1.8 10s.5 3.3 1.3 4.7l3-2.1z" />
      <text x="50" y="14.5" textAnchor="middle" fill="#3C4043" fontSize="8.5" fontWeight="600" fontFamily="Roboto,system-ui,sans-serif">
        Pay
      </text>
    </svg>
  );
}

function SvgKlarna({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 52 20" className="h-5 w-[52px]" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Klarna</title>
      <rect width="52" height="20" rx="3" fill="#FFB3C7" />
      <text x="26" y="13" textAnchor="middle" fill="#0B051D" fontSize="8" fontWeight="800" fontFamily="system-ui,sans-serif">
        Klarna
      </text>
    </svg>
  );
}

function SvgLink({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 44 20" className="h-5 w-11" role="img" aria-labelledby={titleId}>
      <title id={titleId}>Link</title>
      <rect width="44" height="20" rx="3" fill="#00D66F" />
      <text x="22" y="13" textAnchor="middle" fill="#0a2540" fontSize="10" fontWeight="800" fontFamily="system-ui,sans-serif">
        link
      </text>
    </svg>
  );
}

function SvgBlik({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 40 20" className="h-5 w-10" role="img" aria-labelledby={titleId}>
      <title id={titleId}>BLIK</title>
      <rect width="40" height="20" rx="3" fill="#E60028" />
      <text x="20" y="13" textAnchor="middle" fill="white" fontSize="9" fontWeight="800" fontFamily="system-ui,sans-serif">
        BLIK
      </text>
    </svg>
  );
}

function SvgEps({ titleId }: { titleId: string }) {
  return (
    <svg viewBox="0 0 36 20" className="h-5 w-9" role="img" aria-labelledby={titleId}>
      <title id={titleId}>EPS</title>
      <rect width="36" height="20" rx="3" fill="#C8036F" />
      <text x="18" y="13" textAnchor="middle" fill="white" fontSize="10" fontWeight="800" fontFamily="system-ui,sans-serif">
        eps
      </text>
    </svg>
  );
}

const BRAND_RENDER: Record<
  CustomerBrandId,
  (props: { titleId: string }) => ReactElement
> = {
  ideal: SvgIdeal,
  bancontact: SvgBancontact,
  visa: SvgVisa,
  mastercard: SvgMastercard,
  'apple-pay': SvgApplePay,
  'google-pay': SvgGooglePay,
  klarna: SvgKlarna,
  link: SvgLink,
  blik: SvgBlik,
  eps: SvgEps,
};

export function PaymentBrandIcon({ brand, label }: { brand: CustomerBrandId; label: string }) {
  const id = `pay-brand-${brand}`;
  const Svg = BRAND_RENDER[brand];
  return (
    <span className={iconWrap} title={label}>
      <Svg titleId={id} />
    </span>
  );
}

type StripProps = {
  className?: string;
  showMoreLink?: boolean;
  /** Altijd gecentreerd (bijv. footer); anders op brede schermen rechts uitlijnen. */
  centered?: boolean;
};

export function AcceptedPaymentStrip({ className = '', showMoreLink, centered }: StripProps) {
  const justify = centered ? 'justify-center' : 'justify-center sm:justify-end';
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${justify} ${className}`}
      role="group"
      aria-label="Geaccepteerde betaalmethoden voor abonnementen"
    >
      {CUSTOMER_BRAND_ORDER.map((brand) => (
        <PaymentBrandIcon key={brand} brand={brand} label={CUSTOMER_METHOD_COPY[brand].label} />
      ))}
      {showMoreLink ? (
        <Link
          to="/betaalmethoden"
          className="ml-1 text-[10px] font-medium text-primary underline-offset-2 hover:underline sm:text-xs"
        >
          Meer info
        </Link>
      ) : null}
    </div>
  );
}
